// ===== DEEP CONTENT SCANNER =====
// Comprehensive analysis of ALL text content in React components

import { CityLanguage } from '@/context/languages/languageData';
import { TranslationValidationEngine, ValidationIssue, TextFragment } from './TranslationValidationEngine';

export interface ComponentAnalysis {
  filePath: string;
  componentName: string;
  textFragments: TextFragment[];
  hardcodedStrings: string[];
  missingTranslations: string[];
  coveragePercentage: number;
  severity: 'critical' | 'high' | 'medium' | 'low';
}

export interface DeepScanResult {
  totalFiles: number;
  totalComponents: number;
  totalTextFragments: number;
  hardcodedCount: number;
  translatedCount: number;
  overallCoverage: number;
  componentAnalyses: ComponentAnalysis[];
  criticalIssues: ValidationIssue[];
  recommendedFixes: RecommendedFix[];
  estimatedFixTime: string;
}

export interface RecommendedFix {
  type: 'replace_hardcoded' | 'add_translation' | 'fix_language' | 'optimize_seo';
  filePath: string;
  lineNumber?: number;
  originalText: string;
  suggestedFix: string;
  priority: 'high' | 'medium' | 'low';
  effort: 'easy' | 'moderate' | 'complex';
}

export class DeepContentScanner {
  
  // Mock file system scanning - In real implementation, this would scan actual files
  private static MOCK_COMPONENTS = [
    {
      path: 'src/pages/CityPage.tsx',
      content: `
        <div>
          <h1>Find Therapist in Hamburg</h1>
          <p>Professional mental health services</p>
          <Button>Book Appointment</Button>
          <span>Available 24/7</span>
        </div>
      `
    },
    {
      path: 'src/components/city/CityHeroSection.tsx',
      content: `
        <section>
          <h2>Mental Health Professionals</h2>
          <p>Connect with qualified therapists</p>
          <button>Search Now</button>
        </section>
      `
    },
    {
      path: 'src/components/city/CityLanguageSelector.tsx',
      content: `
        <Card>
          <CardTitle>Choose Your Language</CardTitle>
          <p>This page is available in multiple languages</p>
        </Card>
      `
    }
  ];

  static async scanProject(
    targetLanguage: CityLanguage = 'en',
    options: {
      includePaths?: string[];
      excludePaths?: string[];
      scanHidden?: boolean;
      includeComments?: boolean;
    } = {}
  ): Promise<DeepScanResult> {
    console.log(`🔍 Starting deep content scan for language: ${targetLanguage}`);
    
    const componentAnalyses: ComponentAnalysis[] = [];
    let totalTextFragments = 0;
    let hardcodedCount = 0;
    let translatedCount = 0;

    // Simulate scanning each component
    for (const component of this.MOCK_COMPONENTS) {
      const analysis = await this.scanComponent(component.path, component.content, targetLanguage);
      componentAnalyses.push(analysis);
      
      totalTextFragments += analysis.textFragments.length;
      hardcodedCount += analysis.hardcodedStrings.length;
      translatedCount += (analysis.textFragments.length - analysis.hardcodedStrings.length);
    }

    const overallCoverage = totalTextFragments > 0 
      ? Math.round((translatedCount / totalTextFragments) * 100) 
      : 100;

    const criticalIssues = this.identifyCriticalIssues(componentAnalyses);
    const recommendedFixes = this.generateRecommendedFixes(componentAnalyses);

    return {
      totalFiles: this.MOCK_COMPONENTS.length,
      totalComponents: this.MOCK_COMPONENTS.length,
      totalTextFragments,
      hardcodedCount,
      translatedCount,
      overallCoverage,
      componentAnalyses,
      criticalIssues,
      recommendedFixes,
      estimatedFixTime: this.calculateEstimatedFixTime(recommendedFixes)
    };
  }

  private static async scanComponent(
    filePath: string, 
    content: string, 
    targetLanguage: CityLanguage
  ): Promise<ComponentAnalysis> {
    const componentName = this.extractComponentName(filePath);
    const textElements = this.extractTextElements(content);
    
    const textFragments: TextFragment[] = [];
    const hardcodedStrings: string[] = [];
    const missingTranslations: string[] = [];

    for (const { text, element, lineNumber } of textElements) {
      const fragment = TranslationValidationEngine.analyzeTextFragment(text, targetLanguage, {
        filePath,
        element,
        lineNumber
      });
      
      textFragments.push(fragment);
      
      if (!fragment.isTranslated) {
        hardcodedStrings.push(text);
      }
      
      if (fragment.severity === 'critical' || fragment.severity === 'high') {
        missingTranslations.push(text);
      }
    }

    const coveragePercentage = textFragments.length > 0 
      ? Math.round(((textFragments.length - hardcodedStrings.length) / textFragments.length) * 100)
      : 100;

    const severity = this.calculateComponentSeverity(hardcodedStrings.length, textFragments.length);

    return {
      filePath,
      componentName,
      textFragments,
      hardcodedStrings,
      missingTranslations,
      coveragePercentage,
      severity
    };
  }

  private static extractComponentName(filePath: string): string {
    const fileName = filePath.split('/').pop() || '';
    return fileName.replace(/\.(tsx|ts|jsx|js)$/, '');
  }

  private static extractTextElements(content: string): Array<{
    text: string;
    element: string;
    lineNumber: number;
  }> {
    const elements: Array<{ text: string; element: string; lineNumber: number }> = [];
    const lines = content.split('\n');
    
    // Patterns to match different types of text content
    const patterns = [
      // JSX element content: <tag>text</tag>
      /<([a-zA-Z][a-zA-Z0-9]*)[^>]*>([^<]+)<\/\1>/g,
      // String literals: "text" or 'text'
      /["']([^"']+)["']/g,
      // Template literals: `text`
      /`([^`]+)`/g,
      // aria-label and other attributes
      /(aria-label|placeholder|title|alt)=["']([^"']+)["']/g
    ];

    lines.forEach((line, lineIndex) => {
      patterns.forEach(pattern => {
        let match;
        while ((match = pattern.exec(line)) !== null) {
          const text = match[2] || match[1];
          if (text && text.trim().length > 2 && !text.match(/^[{}()[\],.;:!?]+$/)) {
            elements.push({
              text: text.trim(),
              element: this.determineElementType(line),
              lineNumber: lineIndex + 1
            });
          }
        }
      });
    });

    return elements;
  }

  private static determineElementType(line: string): string {
    if (line.includes('<h1')) return 'h1';
    if (line.includes('<h2')) return 'h2';
    if (line.includes('<h3')) return 'h3';
    if (line.includes('<button') || line.includes('<Button')) return 'button';
    if (line.includes('<p>')) return 'p';
    if (line.includes('<span')) return 'span';
    if (line.includes('aria-label')) return 'aria-label';
    if (line.includes('placeholder')) return 'placeholder';
    if (line.includes('title')) return 'title';
    if (line.includes('alt')) return 'alt';
    return 'text';
  }

  private static calculateComponentSeverity(
    hardcodedCount: number, 
    totalCount: number
  ): 'critical' | 'high' | 'medium' | 'low' {
    if (totalCount === 0) return 'low';
    
    const hardcodedPercentage = (hardcodedCount / totalCount) * 100;
    
    if (hardcodedPercentage > 75) return 'critical';
    if (hardcodedPercentage > 50) return 'high';
    if (hardcodedPercentage > 25) return 'medium';
    return 'low';
  }

  private static identifyCriticalIssues(analyses: ComponentAnalysis[]): ValidationIssue[] {
    const criticalIssues: ValidationIssue[] = [];
    
    analyses.forEach(analysis => {
      analysis.textFragments.forEach(fragment => {
        if (fragment.severity === 'critical' || fragment.severity === 'high') {
          criticalIssues.push({
            type: fragment.isTranslated ? 'wrong_language' : 'hardcoded_text',
            severity: fragment.severity,
            fragment: fragment,
            description: `${fragment.isTranslated ? 'Wrong language detected' : 'Hardcoded text found'}: "${fragment.text}"`,
            autoFixable: true,
            suggestedFix: fragment.suggestion
          });
        }
      });
    });

    return criticalIssues.sort((a, b) => {
      const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
      return severityOrder[b.severity] - severityOrder[a.severity];
    });
  }

  private static generateRecommendedFixes(analyses: ComponentAnalysis[]): RecommendedFix[] {
    const fixes: RecommendedFix[] = [];
    
    analyses.forEach(analysis => {
      analysis.hardcodedStrings.forEach(text => {
        const translationKey = this.generateTranslationKey(text);
        fixes.push({
          type: 'replace_hardcoded',
          filePath: analysis.filePath,
          originalText: text,
          suggestedFix: `{t('${translationKey}')}`,
          priority: text.length > 50 || analysis.severity === 'critical' ? 'high' : 'medium',
          effort: 'easy'
        });
      });
      
      analysis.missingTranslations.forEach(text => {
        fixes.push({
          type: 'add_translation',
          filePath: analysis.filePath,
          originalText: text,
          suggestedFix: `Add translation for: "${text}"`,
          priority: 'high',
          effort: 'moderate'
        });
      });
    });

    return fixes.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  }

  private static generateTranslationKey(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '')
      .slice(0, 30);
  }

  private static calculateEstimatedFixTime(fixes: RecommendedFix[]): string {
    const timePerFix = { easy: 2, moderate: 5, complex: 15 }; // minutes
    const totalMinutes = fixes.reduce((total, fix) => total + timePerFix[fix.effort], 0);
    
    if (totalMinutes < 60) return `${totalMinutes} minutes`;
    const hours = Math.round(totalMinutes / 60 * 10) / 10;
    return `${hours} hours`;
  }

  // Method to validate specific route
  static async validateRoute(
    route: string, 
    expectedLanguage: CityLanguage
  ): Promise<ComponentAnalysis[]> {
    console.log(`🔍 Validating route: ${route} for language: ${expectedLanguage}`);
    
    // Mock validation for Hamburg city page
    if (route === '/city/hamburg' || route === '/es/city/hamburg') {
      return await this.validateHamburgPage(expectedLanguage);
    }
    
    return [];
  }

  private static async validateHamburgPage(expectedLanguage: CityLanguage): Promise<ComponentAnalysis[]> {
    const hamburgMockContent = `
      <div>
        <h1>${expectedLanguage === 'es' ? 'Encuentra Terapeuta en Hamburgo' : 'Find Therapist in Hamburg'}</h1>
        <p>${expectedLanguage === 'es' ? 'Servicios profesionales de salud mental' : 'Professional mental health services'}</p>
        <Button>${expectedLanguage === 'es' ? 'Reservar Cita' : 'Book Appointment'}</Button>
        <span>Available 24/7</span>
      </div>
    `;

    const analysis = await this.scanComponent(
      'src/pages/CityPage.tsx', 
      hamburgMockContent, 
      expectedLanguage
    );

    return [analysis];
  }
}